import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
  DialogCloseResult,
  DialogContentBase,
  DialogModule,
  DialogRef, DialogService,
  KENDO_DIALOGS
} from '@progress/kendo-angular-dialog';
import {InputsModule} from '@progress/kendo-angular-inputs';
import {ButtonsModule} from '@progress/kendo-angular-buttons';
import {LabelModule} from '@progress/kendo-angular-label';
import {DrawerItem, DrawerSelectEvent, LayoutModule} from '@progress/kendo-angular-layout';
import {
  AdditionalQuestionOption,
  Assessment,
  AssessmentDomainAnswer, OrganisationDiscipline,
  PlacementReviewStudent
} from '../../models/models.lib';
import {LoggerService} from '../../shared/logger.service';
import {CasiCloseOptions, RESPONSE_TYPES} from '../../common/constants.lib';
import {AssessmentService} from '../assessment.service';
import {Subscription} from 'rxjs';
import { faArrowRight,  faArrowLeft} from '@fortawesome/free-solid-svg-icons';
import {AssessmentIntroductionComponent} from '../assessment-introduction.component/assessment-introduction.component';
import {AssessmentDomainComponent} from '../assessment-domain.component/assessment-domain.component';
import {AssessmentHoursComponent} from '../assessment-hours.component/assessment-hours.component';
import {decimalPattern} from '../../common/utils.lib';
import {
  AssessmentAdditionalAnswerComponent
} from '../assessment-additional-answer.component/assessment-additional-answer.component';
import {ToastrService} from 'ngx-toastr';
import {IconsModule} from '@progress/kendo-angular-icons';
import {ConfirmModalComponent} from '../../common-components/confirm-modal/confirm-modal.component';

@Component({
  selector: 'casi-assessment.component',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DialogModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    LayoutModule,
    AssessmentIntroductionComponent,
    AssessmentAdditionalAnswerComponent,
    AssessmentDomainComponent,
    AssessmentHoursComponent,
    IconsModule,
    KENDO_DIALOGS
  ],
  templateUrl: './assessment.component.html',
  styleUrl: './assessment.component.scss'
})
export class AssessmentComponent  extends DialogContentBase implements OnInit {

  private _myReview: PlacementReviewStudent| undefined;

  @Input({ required: true }) title: string = 'Title Not Set';
  @Input({ required: true }) field: string = '';
  @Input({ required: true }) isDisabled: boolean = false;

  @Input({ required: true }) set myReview(val: PlacementReviewStudent) {
    this._myReview = val;
  }
  get myReview(): PlacementReviewStudent | undefined {
    return this._myReview;
  };

  @Output() assessmentStatus = new EventEmitter<any>();

  private _subs: Subscription[] = [];
  assessment: Assessment = new Assessment();
  public currentIndex = 0;
  public drawerItems: DrawerItem[] = [];
  private firstItem: DrawerItem = {text: "Introduction", selected: true, cssClass: "introduction", icon: ""};
  public selectedItem: DrawerItem = this.firstItem;
  assessmentForm: FormGroup;
  private responseTypes: string[] = RESPONSE_TYPES;

  constructor(
    public override dialog: DialogRef,
    private logger: LoggerService,
    private dataService: AssessmentService,
    public faIcons: FaIconLibrary,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private dialogService: DialogService
  ) {
    super(dialog);
    faIcons.addIcons(
      faArrowLeft,
      faArrowRight
    );

    this.assessmentForm = this.fb.group(
      {
        id: [],
        client_contact_hours: [null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1})?$/)]],
        individual_supervision_hours: [null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1})?$/)]],
        group_supervision_hours: [null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1})?$/)]],
        other_activities_hours: [null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1})?$/)]],
        total_hours: [null],
        domains: this.fb.array([]),
        rating_scale: this.fb.array([]),
        additional_answers: this.fb.array([])
      }
    );
  }

  get f() {return this.assessmentForm.controls;}
  get domainsArray(): FormArray<FormGroup> {
    return this.assessmentForm.get('domains') as FormArray<FormGroup>;
  }

  get scaleArray(): FormArray {
    return (this.assessmentForm.get('rating_scale') as FormArray) ?? this.fb.array([]);
  }

  get additionalAnswersArray(): FormArray<FormGroup> {
    return (this.assessmentForm.get('additional_answers') as FormArray <FormGroup>) ?? this.fb.array([]);
  }

    ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    if(this.myReview && this.myReview.id)
      this.dataService.query(this.myReview.id, this.field, this.myReview.getAssessmentId(this.field  as keyof PlacementReviewStudent));

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      if(e) {
        this.assessment = Assessment.create(e);
        this.assessmentForm.patchValue(this.assessment);

        if (this.isDisabled) {
          this.assessmentForm.get('client_contact_hours')?.disable();
          this.assessmentForm.get('individual_supervision_hours')?.disable();
          this.assessmentForm.get('group_supervision_hours')?.disable();
          this.assessmentForm.get('other_activities_hours')?.disable();
        }

        //domains
        const domainsArray = this.domainsArray;
        domainsArray.clear(); //avoids duplicates
        this.assessment?.domains?.forEach(d => {
          domainsArray.push(this.createDomainGroup(d));
        })

        //rating scale
        const scaleArray = this.scaleArray;
        scaleArray.clear(); //avoids duplicates
        this.assessment?.rating_scale?.forEach(d => {
          scaleArray.push(this.createScaleGroup(d));
        });

        //additionalAnswers
        const additionalAnswersArray = this.additionalAnswersArray;
        additionalAnswersArray.clear(); //avoids duplicates
        this.assessment?.additional_answers?.forEach(d => {
          additionalAnswersArray.push(this.createAdditionalAnswerGroup(d));
        });

        this.assessmentForm.updateValueAndValidity({ emitEvent: false });
        this.drawerItems = this.getMenuItems();
        this._subs.push(
          this.assessmentForm.statusChanges.subscribe(() => {
            this.drawerItems = this.getMenuItems();
            this.updateSelection();
          })
        );
      }
    }));

  }

  public onCancelAction(event?: any): void {
    this.logger.debug(`onCancelAction...`);
    if(this.isDisabled) {
      this.dialog.close({text: CasiCloseOptions.Closed});
      return;
    }
    event?.preventDefault();
    this.openConfirmCancelDialog();
  }

  public onCloseAction(): void {
    this.logger.debug(`onCloseAction...`);
    this.dialog.close({text: CasiCloseOptions.Closed});
  }

  public onSaveAction(): void {
    this.logger.debug(`onSaveAction...`);
    this.save(this.assessmentForm.value);
    this.dialog.close({text: CasiCloseOptions.Saved});
  }

  public onConfirmComplete(): void {
    this.logger.debug("onConfirmComplete: ");
    this.openConfirmCompleteDialog();
  }

  public onSelect(ev: DrawerSelectEvent): void {
    this.selectedItem = ev.item;
    this.currentIndex = ev.index;
    this.drawerItems = this.drawerItems.map(item => ({
      ...item,
      selected: item.text === ev.item.text
    }));
    this.scrollToTop();
  }

  public prev(): void {
    if (this.currentIndex > 0) this.currentIndex--;
    this.updateSelection();
    this.scrollToTop();
  }

  public next(): void {
    if (this.currentIndex < this.drawerItems.length - 1) this.currentIndex++;
    this.updateSelection();
    this.scrollToTop();
  }

  public onOptOutChanged(checked: boolean) {
    this.logger.debug("optOut changed: ", checked);
    if (checked)  this.next();
  }

  getIconClass(item: DrawerItem): string {
    if (!item.icon) return '';

    if (item.icon === 'check-circle')
      return 'icon-valid';

    if (item.icon === 'exclamation-circle')
      return 'icon-invalid';

    return '';
  }

  private save(assessment: any): void {
    this.logger.debug('save...', assessment);
    this.dataService.save(assessment)
      .then(resp => {
        this.logger.debug('save:update:resp: ', resp);
        this.notify("Assessment Updated");

        this.assessmentStatus.emit({
          id: this.assessment?.id,
          data: this.assessmentForm.value,
          status: 'saved'
        });

      })
      .catch(err => {
        this.logger.error('save:update:err: ', err);
        this.notify("Error Updating Assessment", true);
      });
  }

  private openConfirmCancelDialog(): void {
    this.logger.debug("openConfirmCancelDialog: ");
    const dialogRef = this.dialogService.open({
      content: ConfirmModalComponent,
      minWidth: 250,
      width: 450
    });

    const contentRef = dialogRef.content.instance as ConfirmModalComponent;
    contentRef.title = "CONFIRM CANCEL";
    contentRef.content = `<span>If you cancel, your responses will not be saved.</span><br><span> Are you sure you want to cancel?</span>`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmCancelDialog:ok to proceed... ");
        this.dialog.close({ text: CasiCloseOptions.Cancelled });
      }
    });
  }

  private openConfirmCompleteDialog(): void {
    this.logger.debug("openConfirmCompleteDialog: ");
    const dialogRef = this.dialogService.open({
      content: ConfirmModalComponent,
      minWidth: 250,
      width: 450
    });

    const contentRef = dialogRef.content.instance as ConfirmModalComponent;
    contentRef.title = "CONFIRM COMPLETE";
    contentRef.content = `Are you sure you want to complete this assessment?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmCompleteDialog:ok to proceed... ");
        this.complete();
      }
    });
  }

  private complete(): void {
    this.logger.debug(`complete...`);
    this.doComplete(this.assessmentForm.value);
    this.dialog.close({text: CasiCloseOptions.Complete});
  }

  private doComplete(assessment: any): void {
    this.logger.debug('doComplete...', assessment);
    assessment.completed = true;
    this.dataService.save(assessment)
      .then(resp => {
        this.logger.debug('doComplete:update:resp: ', resp);
        this.notify("Assessment Completed");

        this.assessmentStatus.emit({
          id: this.assessment?.id,
          data: this.assessmentForm.value,
          status: 'completed'
        });

      })
      .catch(err => {
        this.logger.error('doComplete:update:err: ', err);
        this.notify("Error Completing Assessment", true);
      });
  }

  private updateSelection(): void {
    this.selectedItem = this.drawerItems[this.currentIndex] || this.firstItem;
    this.drawerItems = this.drawerItems.map((item, index) => ({
      ...item,
      selected: index === this.currentIndex
    }));
  }

  private getMenuItems(): DrawerItem[] {

    let arr: DrawerItem[] = [
      this.firstItem,
      {text: "Hours", cssClass: "hours", icon: this.isHoursValid() ? 'check-circle' : 'exclamation-circle'}
    ];

    const domains = this.domainsArray.controls;

    domains.forEach((d: FormGroup) => {
      arr.push({text: d.get('domain_description')?.value, cssClass: "domain", id: d.get('assessment_domain_id')?.value,
        icon: this.isDomainValid(d) ? 'check-circle' : 'exclamation-circle'});
    });

    if (this.additionalAnswersArray.length > 0)
      arr.push({text: "Additional Questions", cssClass: "additionalQuestion", icon: this.isAdditionalValid() ? 'check-circle' : 'exclamation-circle'});

    return arr;
  }

  private isHoursValid(): boolean {
    const keys = [
      'client_contact_hours',
      'individual_supervision_hours',
      'group_supervision_hours',
      'other_activities_hours'
    ];
    if (this.isDisabled) return true;
    return keys.every(k => this.assessmentForm.get(k)?.valid);
  }


  isDomainValid(group: FormGroup): boolean  {
    return group.get('opt_out')?.value ? true : group.valid;
  }

  private isAdditionalValid(): boolean {
    return this.additionalAnswersArray.controls.every(c => c.valid);
  }

  private createScaleGroup(scale: any): FormGroup {
    return this.fb.group({
      rating_scale_value: [scale.rating_scale_value],
      rating_scale_name: [scale.rating_scale_name],
      rating_scale_description: [scale.rating_scale_description]
    });
  }

  private createDomainGroup(domain: any): FormGroup {
    const group = this.fb.group({
      assessment_domain_id: [domain.assessment_domain_id],
      domain_description: [domain.domain_description],
      opt_out: [
        { value: domain.opt_out, disabled: this.isDisabled }
      ],
      can_opt_out: [domain.can_opt_out],
      comment: [
        { value: domain.comment, disabled: this.isDisabled }
      ],
      self_report_instructions: [domain.self_report_instructions],
      observer_report_instructions: [domain.observer_report_instructions],
      domain_answers: this.fb.array(this.createDomainAnswers(domain.domain_answers || [])),
      domain_instruction: [domain.domain_instruction],
      help_info_id: [domain.help_info_id]
    });
    this.setDomainValidation(group, domain.opt_out);
    return group;
  }

  private setDomainValidation(group: FormGroup, optOut: boolean) {
    const answers = group.get('domain_answers') as FormArray;

    answers.controls.forEach((group: AbstractControl) => {
      const valueControl = group.get('value');

      if (this.isDisabled) {
        valueControl?.disable({ emitEvent: false });
        return;
      }

      if (optOut)
        valueControl?.disable();
      else
        valueControl?.enable();
    });
    group.updateValueAndValidity({ emitEvent: false });
  }

  private createDomainAnswers(questions: AssessmentDomainAnswer[]): FormGroup[] {
    const scale = this.assessment?.rating_scale ?? [];
    const hasScale = scale.length > 0;

    const minScaleValue = hasScale ? Math.min(...scale.map(s => Number(s.rating_scale_value))) : 1;
    const maxScaleValue = hasScale ? Math.max(...scale.map(s => Number(s.rating_scale_value))) : 7;
    const validators = [Validators.required];

    validators.push(Validators.min(minScaleValue));
    validators.push(Validators.max(maxScaleValue));

    return questions.map(q =>
      this.fb.group({
        assessment_domain_answer_id: [q.assessment_domain_answer_id],
        value: [
          {
            value: q.value && q.value >= minScaleValue ? q.value : null,
            disabled: this.isDisabled
          },
          validators
        ],
        question: [q.question],
        sort_order: [q.sort_order]
      })
    );
  }

  private createAdditionalAnswerGroup(answer: any): FormGroup {
    return this.fb.group({
      assessment_additional_answer_id: [answer.assessment_additional_answer_id],
      value: [ { value: answer.value, disabled: this.isDisabled }, this.getAdditionalAnswerValidator(answer)],
      question: [answer.question],
      response_type: [answer.response_type],
      decimal_places: [answer.decimal_places],
      sort_order: [answer.sort_order],
      compulsory: [answer.compulsory],
      options: this.fb.array(this.createAnswerOptions(answer.options || []))
    }, {
      validators: this.atLeastOneCheckboxValidator()
    });
  }

  private createAnswerOptions(options: AdditionalQuestionOption[]): FormGroup[] {
    return options.map(q =>
      this.fb.group({
        option: [q.option],
        sort_order: [q.sort_order]
      })
    );
  }

  private getAdditionalAnswerValidator(answer: any): ValidatorFn {
    const validators: ValidatorFn[] = [];

    if (answer.compulsory)
      validators.push(Validators.required);

    if (answer.response_type === this.responseTypes[1]) {
      validators.push(Validators.min(0));
      validators.push(
        Validators.pattern(decimalPattern(answer.decimal_places))
      );
    }
    return Validators.compose(validators) as ValidatorFn;
  }

  private atLeastOneCheckboxValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {

      const fg = control as FormGroup;
      const responseType = fg.get('response_type')?.value;
      const compulsory = fg.get('compulsory')?.value;

      if (!compulsory || responseType !== this.responseTypes[3])
        return null;

      const rawValue = fg.get('value')?.value;
      const value = this.normalizeCheckboxValue(rawValue);
      const valid = value.some(v => v === true);
      return valid ? null : { requiredSelection: true };
    };
  }

  private normalizeCheckboxValue(value: any): boolean[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [];
      }
    }
    return [value];
  }

  private scrollToTop(): void {
    setTimeout(() => {
      const content = document.querySelector('.dialog-content-wrapper');
      if (content)
        content.scrollTop = 0;
    });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }
}
